import ConfirmationModal from "components/ConfirmationModal";
import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { editSummon } from "services/advocateComplaintService";
import {
  AdvocateComplaints,
  Complaints,
  Investigation,
  Litigation,
  Summon,
} from "types";
import { ADVOCATE_SUMMON_STATUS, COMPLIANT_STATUS } from "utils/constantValues";
import { DateView } from "utils/helper";
import { ADVOCATE_ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";
import AddSummonDecisionModal from "./AddSummonDecisionModal";
import AddSummonResponse from "./AddSummonResponse";
import SummonConfirmationModal from "./SummonConfirmationModal";

interface SummonProps {
  complaintData: AdvocateComplaints;
  summonData: Summon;
  compliantData: Complaints;
  investigationData: Investigation;
  litigationData?: Litigation[];
  onSummonRefresh: any;
  onComplaintsRefresh: any;
}

export default function SummonCard({
  complaintData,
  summonData,
  compliantData,
  investigationData,
  litigationData,
  onSummonRefresh,
  onComplaintsRefresh,
}: SummonProps) {
  const { t } = useTranslation();
  // const canCreateSummonDecision = useHasRole([
  //   ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  //   ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  // ]);
  const canCoordinatorConfirmSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);
  const canAccusedConfirmSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
  ]);
  const canUpdateHearingDate = useHasRole([
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  // const canViewLetterDoc = useHasRole([
  //   ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  //   ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  //   ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  //   ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  //   ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
  //   ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  //   ADVOCATE_ROLES.INSPECTION_REGISTRAR,
  // ]);
  const canViewSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const hideViewLetterFile = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);

  const canViewConfirmedSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);

  // const { user } = useAuth();
  const { showToast } = useToast();

  // const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  // const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isShowResponseModal, setIsShowResponseModal] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [coordinatorConfirmationModal, setCoordinatorConfirmationModal] =
    useState<boolean>(false);
  const [isSendToCouncilModal, setIsSendToCouncilModal] = useState(false);
  const [isEditingHearingDate, setIsEditingHearingDate] = useState(false);
  const [hearingDateDraft, setHearingDateDraft] = useState("");
  const [isUpdatingHearingDate, setIsUpdatingHearingDate] = useState(false);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startHearingDateEdit = () => {
    setHearingDateDraft(toDateInputValue(summonData?.hearing_date));
    setIsEditingHearingDate(true);
  };

  const cancelHearingDateEdit = () => {
    setIsEditingHearingDate(false);
    setHearingDateDraft("");
  };

  const handleUpdateHearingDate = async () => {
    if (!summonData?.id) {
      showToast(t("summonLetterNotFound"), "error");
      return;
    }

    const currentDate = toDateInputValue(summonData?.hearing_date);
    if (!hearingDateDraft || hearingDateDraft === currentDate) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingHearingDate(true);
    try {
      await editSummon(
        {
          hearing_date: hearingDateDraft,
        } as any,
        summonData.id,
        false,
      );

      await Promise.resolve(onSummonRefresh?.());
      cancelHearingDateEdit();
      showToast("Hearing date updated", "success");
    } catch (err: any) {
      showToast(t("submissionError"), "error");
    } finally {
      setIsUpdatingHearingDate(false);
    }
  };

  const handleConfirm = async () => {
    if (!summonData) return;

    // setLoading(true);

    const isAccusedConfirmed = summonData?.accused_confirmed
      ? summonData?.accused_confirmed
      : canAccusedConfirmSummon;

    const isCoordinatorConfirmed = summonData?.coordinator_confirmed
      ? summonData?.coordinator_confirmed
      : canCoordinatorConfirmSummon;

    const data = new FormData();
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("coordinator_confirmed", isCoordinatorConfirmed.toString());

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editSummon(data, summon_id, isFormData);

      onSummonRefresh();
      setShowConfirmationModal(false);
      showToast(t("successfullyConfirmed"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      // setLoading(false);
    }
  };

  const handleSendToCouncilConfirm = async () => {
    if (!isSendToCouncilModal) return;

    if (!summonData) {
      showToast(t("summonLetterNotFound"), "error");
      return;
    }

    setSubmitLoading(true);

    const data = new FormData();
    data.append("status", ADVOCATE_SUMMON_STATUS.FORWARDED_TO_COUNCIL_HEAD);
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editSummon(data, summon_id, isFormData);

      onSummonRefresh();
      setIsSendToCouncilModal(false);
      showToast(t("successfullyCreated"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const accusedSummonFile = summonData?.documents?.filter(
    (a: any) => a?.document_type === "accused_response",
  ) as any;

  const letterFile = summonData?.documents?.filter(
    (a: any) => a?.document_type === "letter_upload",
  ) as any;

  // Hearing date helpers (copied from complaintRequest UI)
  const calculateHearingDays = () => {
    if (!summonData?.hearing_date) return null;
    const hearingDate = new Date(summonData.hearing_date);
    const today = new Date();
    hearingDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const timeDiff = hearingDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  };

  const getHearingStatus = (days: number) => {
    if (days < 0)
      return {
        text: t("hearingDatePassed"),
        color: "text-red-600",
        bg: "bg-red-100",
        border: "border-red-200",
      };
    if (days === 0)
      return {
        text: t("hearingIsToday"),
        color: "text-orange-600",
        bg: "bg-orange-100",
        border: "border-orange-200",
      };
    if (days <= 3)
      return {
        text: t("daysRemaining") + " " + days,
        color: "text-orange-600",
        bg: "bg-orange-100",
        border: "border-orange-200",
      };
    if (days <= 7)
      return {
        text: t("daysRemaining") + " " + days,
        color: "text-yellow-600",
        bg: "bg-yellow-100",
        border: "border-yellow-200",
      };
    return {
      text: t("daysRemaining") + " " + days,
      color: "text-green-600",
      bg: "bg-green-100",
      border: "border-green-200",
    };
  };

  if (!summonData) return null;

  const hearingDays = calculateHearingDays();
  const hearingStatus =
    hearingDays !== null ? getHearingStatus(hearingDays) : null;

  return (
    <div className="mb-8">
      {(canViewSummon ||
        (canViewConfirmedSummon && summonData?.coordinator_confirmed)) &&
        summonData?.hearing_date && (
          <>
            {/* Main Summon Container */}
            <div
              className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4 ${compliantData?.status === COMPLIANT_STATUS.CLOSED || compliantData?.status === COMPLIANT_STATUS.TERMINATED ? "border-red-300" : ""}`}
            >
              {/* Summon Header with Hearing Countdown */}
              <div className="bg-white border-b border-gray-200 px-6 py-4">
                <button
                  onClick={() => {
                    /* keep static header (no collapse) */
                  }}
                  className="w-full text-left focus:outline-none"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-100 border border-red-200 rounded-lg">
                        <svg
                          className="w-5 h-5 text-red-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-800">
                          {t("hearingSummon")}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-600">
                            {t("hearingDate")}:{" "}
                            {summonData?.hearing_date
                              ? DateView(summonData.hearing_date)
                              : "-"}
                          </span>
                          {canUpdateHearingDate && (
                            <button
                              type="button"
                              onClick={startHearingDateEdit}
                              className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                            >
                              {t("updateDate")}
                            </button>
                          )}
                          {hearingStatus && (
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${hearingStatus.bg} ${hearingStatus.color} ${hearingStatus.border}`}
                            >
                              {hearingStatus.text}
                            </span>
                          )}
                        </div>
                        {isEditingHearingDate && (
                          <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t("hearingDate")}
                              </label>
                              <input
                                type="date"
                                value={hearingDateDraft}
                                onChange={(e) =>
                                  setHearingDateDraft(e.target.value)
                                }
                                className="w-full md:w-64 rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={isUpdatingHearingDate}
                                onClick={handleUpdateHearingDate}
                                className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                              >
                                {isUpdatingHearingDate
                                  ? "Updating..."
                                  : "Save Date"}
                              </button>
                              <button
                                type="button"
                                disabled={isUpdatingHearingDate}
                                onClick={cancelHearingDateEdit}
                                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                              >
                                {t("cancel")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              <div className="p-5">
                {/* Accused Prosecutor Info */}
                {complaintData?.assigned_prosecutor && (
                  <div className="mb-6">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="relative">
                        <img
                          src={require("../../../images/avatar.png")}
                          alt="avatar"
                          className="w-12 h-12 rounded-full border-2 border-white shadow-sm"
                        />
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">
                          {complaintData?.assigned_prosecutor}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("assignedProsecutor")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hearing Details */}
                <div className="mb-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("hearingDate")}
                      </p>
                      <p className="text-sm font-medium text-gray-800">
                        {summonData?.hearing_date
                          ? DateView(summonData.hearing_date)
                          : "-"}
                      </p>
                    </div>
                    {hearingStatus && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("status")}
                        </p>
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${hearingStatus.bg} ${hearingStatus.color} ${hearingStatus.border}`}
                        >
                          {hearingStatus.text}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("addressInformation")}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm font-medium text-gray-800 border border-gray-200 shadow-sm p-3 rounded-lg">
                      <div>
                        {t("region")}: {summonData?.region || t("na")}
                      </div>
                      <div>
                        {t("zone")}: {summonData?.zone || t("na")}
                      </div>
                      <div>
                        {t("woreda")}: {summonData?.woreda || t("na")}
                      </div>
                      <div>
                        {t("kebele")}: {summonData?.kebele || t("na")}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summon Summary */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    {t("summonSummary")}
                  </h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {summonData?.summary || t("noSummaryProvided")}
                    </p>
                  </div>
                </div>

                {/* Documents Section */}
                <div className="border-t border-gray-200 pt-6">
                  {summonData?.documents && (
                    <div className="space-y-4">
                      <DocumentViewerModal
                        documents={letterFile || []}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                        layout="list"
                        hide={hideViewLetterFile}
                        label={t("officialLetter")}
                        hideEmptyFile={true}
                      />

                      <DocumentViewerModal
                        documents={
                          summonData?.documents?.filter(
                            (a: any) => a?.document_type === "summon_file",
                          ) as any
                        }
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                        layout="list"
                        label={t("summonDocument")}
                        hideEmptyFile={true}
                      />

                      <DocumentViewerModal
                        documents={accusedSummonFile || []}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                        layout="list"
                        label={t("accusedResponse")}
                        hideEmptyFile={true}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mb-4">
              {/* keep original role-based buttons from advocate file where applicable */}
              {canCoordinatorConfirmSummon &&
                summonData?.status === ADVOCATE_SUMMON_STATUS.SUMMON_ISSUED &&
                !summonData?.coordinator_confirmed && (
                  <button
                    className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
                    type="submit"
                    onClick={() => setCoordinatorConfirmationModal(true)}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {t("confirmSummon")}
                  </button>
                )}

              {/* Removed duplicate "Submit Official Response" button - use the dedicated Add Response section below */}
            </div>
          </>
        )}

      {/* Response and Letter Documents */}
      {(summonData?.letter_upload_details ||
        summonData?.accused_response_details) && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4">
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 border border-red-200 rounded-lg">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-800">
                    {t("officialDocuments")}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t("responseLettersAndCorrespondence")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-6">
            {/* Accused Prosecutor Response */}
            {summonData?.accused_response_details && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  {t("accusedAdvocateResponse")}
                </h4>
                <div className="flex justify-between items-center p-3 bg-red-50 border border-red-100 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-red-100 border border-red-200 rounded">
                      <span className="text-xs font-medium text-red-700">
                        PDF
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {summonData?.accused_response_details?.file_name ||
                          t("responseDocument")}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("officialResponseSubmission")}
                      </p>
                    </div>
                  </div>
                  <FileActions
                    fileUrl={summonData?.accused_response_details?.url || ""}
                  />
                </div>
              </div>
            )}

            {/* Official Letter Document */}
            {summonData?.letter_upload_details && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  {t("officialSummonLetter")}
                </h4>
                <div className="flex justify-between items-center p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-100 border border-blue-200 rounded">
                      <span className="text-xs font-medium text-blue-700">
                        PDF
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {summonData?.letter_upload_details?.file_name ||
                          t("officialLetter")}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("governmentIssuedSummon")}
                      </p>
                    </div>
                  </div>
                  <FileActions
                    fileUrl={summonData?.letter_upload_details?.url || ""}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Response Button for Accused */}
      {canAccusedConfirmSummon &&
        !summonData?.accused_response_details &&
        (!accusedSummonFile || accusedSummonFile.length === 0) && (
          <div className="flex justify-end mt-4">
            <button
              className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
              type="submit"
              onClick={() => setIsShowResponseModal(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              {t("submitAccusedAdvocateResponse")}
            </button>
          </div>
        )}

      {/* Modals */}
      {summonData && (
        <AddSummonDecisionModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          summonData={summonData}
          onRefresh={onSummonRefresh}
        />
      )}

      {summonData && complaintData && investigationData && (
        <SummonConfirmationModal
          isOpen={coordinatorConfirmationModal}
          setIsOpen={setCoordinatorConfirmationModal}
          complaintData={complaintData}
          summonData={summonData}
          investigationData={investigationData}
          onRefresh={onSummonRefresh}
          onComplaintsRefresh={onComplaintsRefresh}
        />
      )}

      {summonData && complaintData && (
        <AddSummonResponse
          isOpen={isShowResponseModal}
          setIsOpen={setIsShowResponseModal}
          summonData={summonData}
          complaintData={complaintData}
          onRefresh={onSummonRefresh}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirm}
        isLoading={submitLoading}
      />
      <ConfirmationModal
        isOpen={isSendToCouncilModal}
        onCancel={() => setIsSendToCouncilModal(false)}
        onConfirm={handleSendToCouncilConfirm}
        isLoading={submitLoading}
      />
    </div>
  );
}
